import { useVideoPlaybackState } from '@app/core/playback/video/useVideoPlaybackState';
import CloseIcon from '@mui/icons-material/Close';
import PictureInPictureIcon from '@mui/icons-material/PictureInPicture';
import { IconButton, Tooltip } from '@mui/material';
import { useCallback, useEffect } from 'react';
import Player from 'video.js/dist/types/player';
import { useLivePlayerSettingsStore } from '../store/live-player-settings.store';

/**
 * Video.js Player options_ does not include enableDocumentPictureInPicture in official typings.
 * Used only for the Document PiP support check (Chrome/Edge 116+).
 */
type PlayerOptionsWithDocPiP = Player['options_'] & {
  enableDocumentPictureInPicture?: boolean;
};

/** Whether PiP is supported for this player (element PiP or Document PiP). */
function isPiPSupported(player: Player | null): boolean {
  if (!player || typeof document === 'undefined') return false;
  const options = player.options_ as PlayerOptionsWithDocPiP | undefined;
  return (
    (document.pictureInPictureEnabled && !player.disablePictureInPicture()) ||
    !!(
      options?.enableDocumentPictureInPicture &&
      typeof window !== 'undefined' &&
      'documentPictureInPicture' in window
    )
  );
}

/**
 * Button to toggle Picture-in-Picture mode for the live player.
 * Uses Video.js PiP API so Document PiP is used on supported browsers (Chrome/Edge 116+)
 * when enableDocumentPictureInPicture is set in player options.
 */
export function LivePlayerPiPControl() {
  const videoJsPlayer = useVideoPlaybackState((s) => s.videoJsPlayer);
  const isPiPActive = useLivePlayerSettingsStore((state) => state.isPiPActive);
  const setIsPiPActive = useLivePlayerSettingsStore((state) => state.setIsPiPActive);
  const player = videoJsPlayer as Player | null;

  useEffect(() => {
    if (!player) return;

    const onEnter = () => setIsPiPActive(true);
    const onLeave = () => setIsPiPActive(false);

    player.on('enterpictureinpicture', onEnter);
    player.on('leavepictureinpicture', onLeave);

    return () => {
      player.off('enterpictureinpicture', onEnter);
      player.off('leavepictureinpicture', onLeave);
    };
  }, [player, setIsPiPActive]);

  const handleTogglePiP = useCallback(async () => {
    if (!player) return;
    try {
      if (player.isInPictureInPicture()) {
        await player.exitPictureInPicture();
      } else {
        await player.requestPictureInPicture();
      }
    } catch {
      setIsPiPActive(!!player.isInPictureInPicture());
    }
  }, [player, setIsPiPActive]);

  if (!player || !isPiPSupported(player)) {
    return null;
  }

  return (
    <Tooltip title={isPiPActive ? 'Salir de PiP' : 'Picture-in-Picture'}>
      <IconButton size="small" onClick={handleTogglePiP}>
        {isPiPActive ? <CloseIcon fontSize="small" /> : <PictureInPictureIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}
